import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Mail, ShieldAlert } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { RelayMark, BrandLockup } from "@/components/brand";
import { Btn, Field, Input, Panel } from "@/components/kit";

const DEV_ACCOUNTS = [
  { email: "ae.pod1@venson.dev", label: "Priya Anand", role: "Account Executive", pod: "Pod 1" },
  { email: "ae.pod2@venson.dev", label: "Marcus Bell", role: "Account Executive", pod: "Pod 2" },
  { email: "ae.pod3@venson.dev", label: "Sofia Ruiz", role: "Account Executive", pod: "Pod 3" },
  { email: "ae.pod4@venson.dev", label: "Daniel Kerr", role: "Account Executive", pod: "Pod 4" },
  { email: "ae.support@venson.dev", label: "Ruth Merrick", role: "Senior Account Executive", pod: "Support Services" },
  { email: "ae1@venson.dev", label: "Nina Osei", role: "Account Executive", pod: "Pod 1" },
  { email: "ae2@venson.dev", label: "Tom Whitfield", role: "Account Executive", pod: "Pod 2" },
  { email: "am@venson.dev", label: "Account Manager", role: "Account Manager", pod: "All customers" },
  { email: "podlead@venson.dev", label: "Pod Lead", role: "Pod Lead", pod: "Pod 1" },
  { email: "admin@venson.dev", label: "Administrator", role: "Administrator", pod: "All pods" },
  { email: "fm.sanctuary@fleet.dev", label: "Fleet Manager", role: "External FM", pod: "Sanctuary" },
  { email: "fm.platform@fleet.dev", label: "Fleet Manager", role: "External FM", pod: "Platform" },
];

export default function StaffLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [devEnabled, setDevEnabled] = useState(false);

  useEffect(() => {
    api.devLoginEnabled().then((r) => setDevEnabled(!!r.enabled)).catch(() => setDevEnabled(false));
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { shouldCreateUser: false },
      });
      if (error) throw error;
      setStep(2);
      setCooldown(60);
      toast.success("If that email is registered, a sign-in code is on its way.");
    } catch (err) {
      // Generic: never reveal whether the email exists
      setStep(2);
      setCooldown(60);
      toast.success("If that email is registered, a sign-in code is on its way.");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length < 6) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(), token: code.trim(), type: "email",
      });
      if (error || !data?.session) throw error || new Error("No session");
      toast.success("Signed in.");
      nav("/", { replace: true });
    } catch (err) {
      toast.error("That code did not match or has expired. Please try again.");
    } finally { setBusy(false); }
  };

  const devLogin = async (acctEmail) => {
    setBusy(true);
    try {
      const s = await api.devLogin(acctEmail);
      const { error } = await supabase.auth.setSession({ access_token: s.access_token, refresh_token: s.refresh_token });
      if (error) throw error;
      toast.success("Development sign-in.");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err.message || "Development sign-in failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[1.1fr_1fr]">
      <aside className="hidden lg:flex flex-col justify-between border-r border-border bg-card p-12" data-testid="login-desktop-aside">
        <BrandLockup />
        <div>
          <p className="vda-tick text-[hsl(var(--vda-clay))] mb-3">Staff console</p>
          <h1 className="text-4xl font-semibold tracking-tight leading-tight max-w-md">The operational control layer for fleet teams.</h1>
          <p className="mt-4 text-muted-foreground max-w-md">Queues, bookings, vehicle history, ingestion and out-of-hours — one restrained, auditable workspace.</p>
        </div>
        <p className="vda-tick text-muted-foreground">AVENIR relay. · secure passwordless sign-in</p>
      </aside>
      <div className="mx-auto w-full max-w-md px-5 py-8 lg:min-h-screen lg:flex lg:flex-col lg:justify-center vda-rise">
        <button onClick={() => (step === 2 ? setStep(1) : nav("/"))} className="flex items-center gap-1 text-sm text-muted-foreground mb-8">
          <ChevronLeft className="h-4 w-4" /> {step === 2 ? "Change email" : "Home"}
        </button>
        <div className="mb-8 lg:hidden">
          <BrandLockup />
          <div className="mt-3 flex items-baseline gap-2 text-muted-foreground text-sm">
            Staff console
          </div>
        </div>

        {step === 1 && (
          <form onSubmit={sendCode} className="space-y-4" data-testid="login-form">
            <p className="text-sm text-foreground/80">
              Enter your work email. We'll send a secure sign-in code — no password required.
            </p>
            <Field label="Email">
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@venson.dev" autoFocus />
            </Field>
            <Btn data-testid="login-send-code" type="submit" loading={busy} className="w-full">
              <Mail className="h-4 w-4" /> Email me a sign-in code
            </Btn>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verify} className="space-y-4" data-testid="login-code-form">
            <p className="text-sm text-foreground/80">
              Enter the six-digit code sent to <span className="font-medium text-foreground">{email}</span>.
            </p>
            <Field label="Sign-in code">
              <Input data-testid="login-otp" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" placeholder="000000" className="vda-reg tracking-[0.5em] text-center text-2xl" autoFocus />
            </Field>
            <Btn data-testid="login-verify" type="submit" loading={busy} className="w-full">Verify &amp; sign in</Btn>
            <button type="button" disabled={cooldown > 0} onClick={sendCode}
              className="w-full text-sm text-muted-foreground hover:text-foreground disabled:opacity-50">
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
          </form>
        )}

        {devEnabled && (
          <div className="mt-10" data-testid="dev-login-panel">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="h-4 w-4 text-[hsl(var(--vda-clay))]" />
              <p className="vda-tick text-[hsl(var(--vda-clay))]">Development access only · disabled in production</p>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Seeded accounts use non-deliverable domains, so email codes can't be received here. One-tap sign-in below is dev-only.
            </p>
            <Panel className="divide-y divide-border">
              {DEV_ACCOUNTS.map((a) => (
                <button key={a.email} data-testid={`dev-acct-${a.email}`} disabled={busy}
                  onClick={() => devLogin(a.email)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-secondary text-sm disabled:opacity-50">
                  <span className="min-w-0">
                    <span className="block font-medium truncate">{a.label}</span>
                    <span className="block text-[11px] text-muted-foreground">{a.role} · {a.pod}</span>
                  </span>
                  <span className="vda-reg text-xs text-muted-foreground shrink-0">{a.email}</span>
                </button>
              ))}
            </Panel>
            <div className="mt-3 flex gap-2" data-testid="dev-custom-login">
              <Input data-testid="dev-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="ae.pod1.a@uat.relay.invalid" className="flex-1" />
              <Btn variant="outline" data-testid="dev-email-login" disabled={busy || !email.trim()}
                onClick={() => devLogin(email.trim().toLowerCase())}>Dev sign-in</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
